import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { BodyRegular, BodySmall, BodyVerySmall } from '../../atoms/fonts/Body';
import Colours from '../../atoms/Colours';
import TextField from '../../atoms/TextField';
import { Switch } from '../../atoms/Switch';
import Icon, { IconTypes } from '../../atoms/icon/Icon';
import { PrimaryButton } from '../../atoms/buttons/PrimaryButton';
import { OutlineButton } from '../../atoms/buttons/OutlineButton';
import { ButtonSize } from '../../atoms/buttons/ButtonStyling';
import { GenericDropDownList } from '../../molecules/GenericDropDownList';
import { UserAvatarAndName } from '../../molecules/UserAvatarAndName';
import { UserSearchDropDownList } from '../../molecules/UserSearchDropDownList';
import { DepartmentsMultiSelectDropDownList } from '../DepartmentsMultiSelectDropDownList';
import { DAYS_OF_WEEK, SEND_TIMES } from './ReportScheduleSetting';
import { TIMEZONES } from '@shared/utils/Timezones';
import { Department } from '@features/whereabouts/pages/departments/data/departments.models';
import { BasicUserInfo } from '@shared/models/user.models';
import {
  AbsenceReportFrequency,
  AbsenceReportRecipient,
  ScheduledAbsenceReport
} from '@shared/models/report.models';

const SCHEDULE_DESCRIPTIONS: Record<AbsenceReportFrequency, string> = {
  [AbsenceReportFrequency.DAILY]: 'settings.absence-reports.schedule-daily',
  [AbsenceReportFrequency.WEEKLY]: 'settings.absence-reports.schedule-weekly',
  [AbsenceReportFrequency.MONTHLY]: 'settings.absence-reports.schedule-monthly',
};

/**
 * Create or edit one saved absence report, laid out as a single column so every control lines up.
 * Purely a form: the owning setting saves it, so a failed save keeps everything the admin typed.
 */
export function AbsenceReportEditor(props: Props) {
  const { initial, busy, error, onSave, onCancel } = props;
  const { t } = useTranslation();
  const [report, setReport] = useState<ScheduledAbsenceReport>(initial);
  const [recipients, setRecipients] = useState<AbsenceReportRecipient[]>(initial.recipients ?? []);
  // Remounts the search box after each pick so it is empty for the next person.
  const [searchKey, setSearchKey] = useState(0);

  const frequencyItems = [
    { id: AbsenceReportFrequency.DAILY, name: t('settings.absence-reports.daily') },
    { id: AbsenceReportFrequency.WEEKLY, name: t('settings.absence-reports.weekly') },
    { id: AbsenceReportFrequency.MONTHLY, name: t('settings.absence-reports.monthly') },
  ];

  const canSave = report.name.trim().length > 0 && report.departmentIds.length > 0 && recipients.length > 0;

  const onDepartmentsSelected = (departments: Department[]) => {
    setReport({ ...report, departmentIds: (departments ?? []).map(department => department.id) });
  };

  const onRecipientSelected = (user: BasicUserInfo) => {
    if (user && !recipients.some(recipient => recipient.userId === user.userId)) {
      setRecipients([...recipients, { userId: user.userId, firstName: user.firstName, lastName: user.lastName }]);
    }
    setSearchKey(key => key + 1);
  };

  const removeRecipient = (userId: string) => {
    setRecipients(recipients.filter(recipient => recipient.userId !== userId));
  };

  const save = () => {
    if (!canSave || busy) return;
    onSave({ ...report, name: report.name.trim(), recipientUserIds: recipients.map(recipient => recipient.userId) });
  };

  return (
    <EditorBox>
      <EditorHeader>
        <BodyRegular weight={600}>
          {t(report.id ? 'settings.absence-reports.edit-title' : 'settings.absence-reports.new-title')}
        </BodyRegular>
        <HeaderSwitch>
          <BodySmall colour={Colours.darkGrey}>{t('settings.absence-reports.enabled')}</BodySmall>
          <Switch value={report.enabled} label="" onChange={(enabled: boolean) => setReport({ ...report, enabled })} />
        </HeaderSwitch>
      </EditorHeader>

      <Form>
        <div>
          <FieldLabel weight={600}>{t('settings.absence-reports.label-name')}</FieldLabel>
          <TextField fullWidth={true}
                     placeholder={'settings.absence-reports.name-placeholder'}
                     value={report.name}
                     disabled={busy}
                     onChange={(name: string) => setReport({ ...report, name })} />
        </div>

        <div>
          <FieldLabel weight={600}>{t('settings.absence-reports.label-schedule')}</FieldLabel>
          <ScheduleRow>
            <GenericDropDownList className={'control control--frequency'}
                                 items={frequencyItems}
                                 selectedId={report.frequency}
                                 placeholder={'settings.absence-reports.frequency'}
                                 disabled={busy}
                                 onSelect={(frequency: any) => setReport({ ...report, frequency: frequency as AbsenceReportFrequency })} />
            {report.frequency === AbsenceReportFrequency.WEEKLY && (
              <GenericDropDownList className={'control control--day'}
                                   items={DAYS_OF_WEEK}
                                   selectedId={report.sendDay}
                                   placeholder={'settings.reports-schedule.day'}
                                   disabled={busy}
                                   onSelect={(day: any) => setReport({ ...report, sendDay: parseInt(day) })} />
            )}
            <BodyRegular className={'scheduleRow__at'}>{t('settings.reports-schedule.time')}</BodyRegular>
            <GenericDropDownList className={'control control--time'}
                                 items={SEND_TIMES}
                                 selectedId={report.sendTime}
                                 placeholder={'settings.reports-schedule.time'}
                                 disabled={busy}
                                 onSelect={(sendTime: any) => setReport({ ...report, sendTime })} />
          </ScheduleRow>
          <GenericDropDownList className={'control control--timezone'}
                               items={TIMEZONES}
                               selectedId={report.timezoneId}
                               placeholder={'settings.reports-schedule.timezone'}
                               disabled={busy}
                               onSelect={(timezoneId: any) => setReport({ ...report, timezoneId })} />
          <Help colour={Colours.darkGrey}>{t(SCHEDULE_DESCRIPTIONS[report.frequency])}</Help>
        </div>

        <div className={'absenceReport__departments'}>
          <FieldLabel weight={600}>{t('settings.absence-reports.label-departments')}</FieldLabel>
          <DepartmentsMultiSelectDropDownList label={''}
                                              selectedDepartmentIds={report.departmentIds}
                                              disabled={busy}
                                              onSelect={onDepartmentsSelected} />
        </div>

        <div>
          <FieldLabel weight={600}>{t('settings.absence-reports.label-send-to')}</FieldLabel>
          <UserSearchDropDownList key={searchKey} closeOnSelect={true} disabled={busy} onSelect={onRecipientSelected} />
          {recipients.length > 0 && (
            <RecipientList>
              {recipients.map(recipient => (
                <RecipientRow key={recipient.userId}>
                  <UserAvatarAndName userId={recipient.userId} firstName={recipient.firstName}
                                     lastName={recipient.lastName} disableHover={true} />
                  <RemoveButton type="button"
                                disabled={busy}
                                aria-label={t('settings.absence-reports.remove-recipient', {
                                  name: `${recipient.firstName} ${recipient.lastName}`,
                                  interpolation: { escapeValue: false },
                                })}
                                onClick={() => removeRecipient(recipient.userId)}>
                    <Icon icon={IconTypes.Close} />
                  </RemoveButton>
                </RecipientRow>
              ))}
            </RecipientList>
          )}
          <Help colour={Colours.darkGrey}>{t('settings.absence-reports.recipients-help')}</Help>
        </div>
      </Form>

      {error && <ErrorBox>{error}</ErrorBox>}

      <Footer>
        <BodyVerySmall colour={Colours.darkGrey} className={'footer__hint'}>
          {canSave ? '' : t('settings.absence-reports.missing-fields')}
        </BodyVerySmall>
        <FooterButtons>
          <OutlineButton text={'settings.absence-reports.cancel'} size={ButtonSize.Small} disabled={busy} click={onCancel} />
          <PrimaryButton text={'settings.absence-reports.save'} size={ButtonSize.Small} disabled={!canSave || busy} click={save} />
        </FooterButtons>
      </Footer>
    </EditorBox>
  );
}

interface Props {
  initial: ScheduledAbsenceReport;
  busy: boolean;
  error: string | null;
  onSave: (report: ScheduledAbsenceReport) => void;
  onCancel: () => void;
}

const EditorBox = styled.div`
  margin-top: 16px;
  padding: 16px;
  border: 1px solid ${Colours.blue20};
  border-radius: 8px;
  background-color: ${Colours.white};
`;

const EditorHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding-bottom: 12px;
  margin-bottom: 20px;
  border-bottom: 1px solid ${Colours.lightGrey};

  p {
    margin: 0;
  }
`;

const HeaderSwitch = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

/** One column, so the name, schedule, departments and people all share the same width. */
const Form = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  max-width: 560px;

  .textField,
  .teamDropDownList {
    margin: 0;
  }
  .absenceReport__departments .textField__label {
    display: none;
  }
`;

const FieldLabel = styled(BodySmall)`
  margin: 0 0 6px;
`;

const Help = styled(BodyVerySmall)`
  margin: 6px 0 0;
`;

const ScheduleRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;

  .control {
    width: auto;
    min-width: 0;
  }
  .control--frequency {
    flex: 1 1 140px;
  }
  .control--day {
    flex: 1 1 140px;
  }
  .control--time {
    flex: 0 0 110px;
  }
  .scheduleRow__at {
    margin: 0;
  }
`;

const RecipientList = styled.div`
  margin-top: 8px;
  border: 1px solid ${Colours.lightGrey};
  border-radius: 6px;
`;

const RecipientRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 6px 8px 6px 12px;

  & + & {
    border-top: 1px solid ${Colours.lightGrey};
  }
`;

const RemoveButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;

  .icon {
    color: ${Colours.darkGrey};
  }
  &:hover:not(:disabled) {
    background: rgba(240, 34, 79, 0.08);
    .icon {
      color: ${Colours.red};
    }
  }
  &:focus-visible {
    outline: 2px solid ${Colours.blue};
    outline-offset: 2px;
  }
  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

const ErrorBox = styled.div`
  margin-top: 16px;
  padding: 8px 12px;
  border: 1px solid ${Colours.red};
  color: ${Colours.red};
  border-radius: 4px;
  font-size: 13px;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid ${Colours.lightGrey};

  .footer__hint {
    margin: 0;
    flex: 1 1 240px;
  }
`;

const FooterButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;

  button {
    width: auto;
    min-width: 110px;
  }
`;
